// Browser tab titles follow ui-ux-standard rule 13: most specific segment first, site name last,
// joined by " | ". Shared by the runtime app (App.tsx's route defaults, each resource page's own
// override) and the pre-render step (communityListTitle/communityTemplateTitle in seo.ts, used by
// prerender.tsx), so a page's initial static <title> and its post-hydration document.title can
// never diverge in shape - they were previously built by two independent inline templates using
// two different separators (" - " at runtime, " — " in prerender) for the same community pages.
//
// Hand-authored SEO marketing copy (MARKETING_PAGES in seo.ts - the landing page, /how-it-works,
// /contact, etc.) is out of scope: those titles are bespoke strings a person wrote for search
// ranking, not a mechanical "{page} | Site" template, and are left untouched.

export const SITE_NAME = "NeatTick";
const SEPARATOR = " | ";

const PAGE_TITLES: Record<string, string> = {
  "/login": "Sign in",
  "/register": "Sign up",
  "/verify": "Verify account",
  "/forgot": "Reset password",
  "/profile": "Profile",
  "/lists": "My Lists",
  "/templates": "My Templates",
  "/checklists": "My Checklists",
  "/admin": "Admin",
  "/admin/settings": "Admin settings",
  "/admin/ai-costs": "AI costs",
  "/admin/signups": "Sign-ups",
  "/admin/waitlist": "Access requests",
};

/** Short tab-title segment for a pathname. Marketing pages keep their own copy. */
export function routeTitle(path: string): string {
  if (path === "/community") return "Community checklists";
  const exact = PAGE_TITLES[path];
  if (exact) return exact;
  if (/^\/lists\/new$/.test(path)) return "New list";
  if (/^\/templates\/new$/.test(path)) return "New template";
  if (/^\/lists\/[^/]+$/.test(path)) return "List";
  if (/^\/templates\/[^/]+$/.test(path)) return "Template";
  if (/^\/checklists\/[^/]+$/.test(path)) return "Checklist";
  if (/^\/community\/templates\/[^/]+$/.test(path)) return "Community template";
  if (/^\/community\/[^/]+$/.test(path)) return "Community checklist";
  if (/^\/invite\//.test(path)) return "Invitation";
  if (/^\/request-access\//.test(path)) return "Request access";
  if (/^\/share-decision\//.test(path)) return "Share access";
  if (/^\/s\//.test(path)) return "Shared checklist";
  return "Page not found";
}

/** Joins segments most-specific-first and appends the site name. Blank segments are dropped. */
export function formatPageTitle(...segments: Array<string | null | undefined>): string {
  const parts = segments
    .map((segment) => segment?.trim())
    .filter((segment): segment is string => Boolean(segment));
  return [...parts, SITE_NAME].join(SEPARATOR);
}
